/**
 * Workspace Chrome Provider
 * Manages workspace UI chrome elements: breadcrumbs and workspace actions
 * Allows panels to dynamically set their own breadcrumbs and action buttons
 */

import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import type { BreadcrumbItem } from '../components/common';

interface WorkspaceChromeContextType {
  customBreadcrumbs: BreadcrumbItem[] | null;
  setCustomBreadcrumbs: (breadcrumbs: BreadcrumbItem[] | null) => void;
  workspaceActions: ReactNode | null;
  setWorkspaceActions: (actions: ReactNode | null) => void;
  isPanelExpanded: boolean;
  setIsPanelExpanded: (expanded: boolean | ((prev: boolean) => boolean)) => void;
}

const WorkspaceChromeContext = createContext<WorkspaceChromeContextType | undefined>(undefined);

export const useWorkspaceChrome = (): WorkspaceChromeContextType => {
  const context = useContext(WorkspaceChromeContext);
  if (!context) {
    throw new Error('useWorkspaceChrome must be used within WorkspaceChromeProvider');
  }
  return context;
};

interface WorkspaceChromeProviderProps {
  children: ReactNode;
}

export const WorkspaceChromeProvider: React.FC<WorkspaceChromeProviderProps> = ({ children }) => {
  const [customBreadcrumbs, setCustomBreadcrumbs] = useState<BreadcrumbItem[] | null>(null);
  const [workspaceActions, setWorkspaceActions] = useState<ReactNode | null>(null);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);

  const value = useMemo(() => ({
    customBreadcrumbs,
    setCustomBreadcrumbs,
    workspaceActions,
    setWorkspaceActions,
    isPanelExpanded,
    setIsPanelExpanded,
  }), [customBreadcrumbs, workspaceActions, isPanelExpanded]);

  return (
    <WorkspaceChromeContext.Provider value={value}>
      {children}
    </WorkspaceChromeContext.Provider>
  );
};
